import { QuoteState } from '../../api/dealer-api-interface-quote';
import { BranchQuoteSupportStatus } from '../../api/dealer-api-interface-franchisee';
import { flagInSet } from '../../../webmodule-common/other/ui/string-helper-functions';

// Centralized policy for support ticket read-only states.
// Phase: Quick Win extraction (precedes broader service layer work).
// Future extension: accept user role / permissions context.

export interface SupportReadonlyContext {
  quoteState: QuoteState;
  ticketStatus?: BranchQuoteSupportStatus | null;
}

export function computeSupportReadonlyQuote(quoteState: QuoteState): boolean {
  // eslint-disable-next-line no-bitwise
  return flagInSet(quoteState, QuoteState.Cancelled | QuoteState.Rejected | QuoteState.Approved);
}

export function computeSupportReadonly(ctx: SupportReadonlyContext): boolean {
  // Quote-level readonly first
  if (computeSupportReadonlyQuote(ctx.quoteState)) return true;
  if (ctx.ticketStatus == null) return false;
  // Ticket-specific terminal states
  // eslint-disable-next-line no-bitwise
  return flagInSet(ctx.ticketStatus, BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved);
}

// --- Layered Evaluation Model (Phase A) ---

export enum ReadonlyReason {
  SystemOffline = 'system-offline',
  ResourceLocked = 'resource-locked',
  QuoteLifecycle = 'quote-lifecycle',
  TicketTerminalState = 'ticket-terminal',
  UserPermission = 'user-permission',
  TransientBusy = 'transient-busy'
}

export interface SupportReadonlyCapabilityContext extends SupportReadonlyContext {
  systemOffline: boolean;
  resourceLocked: boolean;
  transientBusy: boolean;
  userPermissions: {
    canEdit: boolean;
    canReactivate: boolean;
    canChangeStatus: boolean;
  };
}

export interface SupportActionsCapability {
  addNote: boolean;
  addAttachment: boolean;
  changeStatus: boolean;
  reactivate: boolean;
  cancel: boolean;
  resolve: boolean;
}

export interface SupportReadonlyEvaluation {
  readonly: boolean;
  reasons: ReadonlyReason[];
  actions: SupportActionsCapability;
}

const denyAll = (): SupportActionsCapability => ({
  addNote: false,
  addAttachment: false,
  changeStatus: false,
  reactivate: false,
  cancel: false,
  resolve: false
});

export function evaluateSupportReadonly(ctx: SupportReadonlyCapabilityContext): SupportReadonlyEvaluation {
  const reasons: ReadonlyReason[] = [];

  if (ctx.systemOffline) reasons.push(ReadonlyReason.SystemOffline);
  if (ctx.resourceLocked) reasons.push(ReadonlyReason.ResourceLocked);
  if (computeSupportReadonlyQuote(ctx.quoteState)) reasons.push(ReadonlyReason.QuoteLifecycle);

  const ticketTerminal =
    !!ctx.ticketStatus &&
    // eslint-disable-next-line no-bitwise
    flagInSet(ctx.ticketStatus, BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved);
  if (ticketTerminal) reasons.push(ReadonlyReason.TicketTerminalState);

  if (!ctx.userPermissions.canEdit) reasons.push(ReadonlyReason.UserPermission);
  if (ctx.transientBusy) reasons.push(ReadonlyReason.TransientBusy);

  // Base capabilities (optimistic) when fully editable
  let actions: SupportActionsCapability = {
    addNote: true,
    addAttachment: true,
    changeStatus: ctx.userPermissions.canChangeStatus,
    reactivate: false,
    cancel: ctx.userPermissions.canChangeStatus,
    resolve: ctx.userPermissions.canChangeStatus
  };

  // Blanket restrict tiers (hard reasons)
  if (
    reasons.includes(ReadonlyReason.SystemOffline) ||
    reasons.includes(ReadonlyReason.ResourceLocked) ||
    reasons.includes(ReadonlyReason.QuoteLifecycle) ||
    reasons.includes(ReadonlyReason.UserPermission) ||
    reasons.includes(ReadonlyReason.TransientBusy)
  ) {
    return { readonly: true, reasons, actions: denyAll() };
  }

  // Ticket terminal only (reactivation allowed)
  if (ticketTerminal) {
    actions.addNote = false;
    actions.addAttachment = false;
    actions.changeStatus = false;
    actions.cancel = false;
    actions.resolve = false;
    actions.reactivate = ctx.userPermissions.canReactivate;
    return { readonly: true, reasons, actions };
  }

  // Editable
  return { readonly: false, reasons, actions };
}
